/**
 * MET — Metriek (Meten & Meetkunde) rulebook.
 *
 * Sourced from DEFAULT_POLICY (Metriek entries) +
 * docs/audits/DIDACTISCH_AUDIT_MET_2026.md + MODULE_AUTHORING_GUIDE §3.
 */

import type { ModuleRulebook } from './types'

export const MET_RULEBOOK: ModuleRulebook = {
  moduleId: 'Metriek',
  shortCode: 'MET',
  displayName: 'Metriek',

  didactics: {
    principles: [
      'eenheid_benoemen',
      'formule_expliciet',
      'metrieke_trap',
      'visueel_meten',
    ],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt'],
    misconceptionDomain: 'met',
    coreSummary:
      'Meten en meetkunde: eenheden, omrekenen via de metrieke trap, formules voor omtrek/opp/inhoud, en meetkundige eigenschappen.',
  },

  notation: {
    requireCommaDecimals: true,
    requireSuperscriptUnits: true,
    forbiddenPatterns: [
      {
        pattern: '\\bcm2\\b|\\bm2\\b|\\bkm2\\b',
        label: 'Eenheden zonder superscript (gebruik cm², m², km²)',
      },
      {
        pattern: '\\bcm3\\b|\\bm3\\b',
        label: 'Eenheden zonder superscript (gebruik cm³, m³)',
      },
    ],
  },

  content: {
    distractorMisconceptionPattern:
      'formule|oppervlakte|omtrek|inhoud|eenheid|omrekenen',
  },

  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: null,
    preferredStrategyKeywords: [
      'metrieke trap',
      'omrekenen',
      'formule',
      'eenheid',
    ],
  },

  visuals: {
    preferredAidTypes: [
      'geometricShape',
      'compositeShape',
      'circle',
      'stairs',
      'ruler',
      'protractor',
      'measuringCup',
      'weighScale',
      'box3d',
      'geo',
      'symmetryGrid',
      'ratioTable',
    ],
    familyVisualHints: [
      // — Hoeken (specific before generic ^MET-HOEK-) —
      {
        familyIdPattern: '^MET-HOEK-METEN$',
        preferredStimulus: 'protractor',
        note: 'Hoeken meten: gradenboog op de hoek.',
      },
      {
        familyIdPattern: '^MET-HOEK-TEKENEN$',
        preferredStimulus: 'protractor',
        note: 'Hoek tekenen met de gradenboog.',
      },
      {
        familyIdPattern: '^MET-HOEK-BEREKENEN$',
        preferredStimulus: 'geometricShape',
        note: 'Hoek-berekening op een figuur (driehoek/vierhoek).',
      },
      {
        familyIdPattern: '^MET-HOEK-SOORTEN',
        preferredStimulus: 'geometricShape',
        note: 'Soorten hoeken visueel onderscheiden.',
      },
      {
        familyIdPattern: '^MET-HOEK-',
        preferredStimulus: 'protractor',
        note: 'Default: hoeken via gradenboog.',
      },

      // — Lengte —
      {
        familyIdPattern: '^MET-LEN-(METEN|NAUWKEURIG)$',
        preferredStimulus: 'ruler',
        note: 'Lengte meten met liniaal.',
      },
      {
        familyIdPattern: '^MET-LEN-SCHATTEN$',
        preferredStimulus: 'ruler',
        note: 'Schatten met referentie-liniaal.',
      },
      {
        familyIdPattern: '^MET-LEN-OMREKEN',
        preferredSupport: 'stairs',
        roleScope: 'rule-only',
        note: 'Lengte-omrekenen via metrieke trap.',
      },

      // — Gewicht —
      {
        familyIdPattern: '^MET-GEW-WEGEN$',
        preferredStimulus: 'weighScale',
        note: 'Gewicht aflezen op weegschaal.',
      },
      {
        familyIdPattern: '^MET-GEW-OMREKEN',
        preferredSupport: 'stairs',
        roleScope: 'rule-only',
        note: 'Gewicht-omrekenen via metrieke trap.',
      },

      // — Inhoud —
      // INH-LITER/CITER zijn tier-9 brug-families: het gaat primair om
      // omrekenen (l ↔ ml ↔ dl ↔ m³), niet om aflezen. De metrieke trap
      // is dus het didactisch dragende beeld; maatbeker mag als variant
      // (één á twee vragen) maar is geen preferred stimulus.
      {
        familyIdPattern: '^MET-INH-(LITER|CITER)$',
        preferredStimulus: 'stairs',
        note: 'Inhoud-brug via metrieke trap (l ↔ ml ↔ dl ↔ m³). Maatbeker mag als variant.',
      },
      {
        familyIdPattern: '^MET-INH-BLOK$',
        preferredStimulus: 'box3d',
        note: 'Inhoud van een blok in 3D tonen.',
      },
      {
        familyIdPattern: '^MET-INH-OMREKEN',
        preferredSupport: 'stairs',
        roleScope: 'rule-only',
        note: 'Inhoud-omrekenen via metrieke trap (lineair en kubiek).',
      },
      {
        familyIdPattern: '^MET-INH-SCHATTEN$',
        preferredStimulus: 'measuringCup',
        note: 'Inhoud schatten met referentie (maatbeker / vertrouwde houder).',
      },

      // — Meetkunde (cirkel-specifiek vóór generic ^MET-MEETK-) —
      {
        familyIdPattern: '^MET-MEETK-(CIRKEL-DEEL|DIAMETER|OMTREK)$',
        preferredStimulus: 'circle',
        note: 'Cirkel met gemarkeerde diameter/straal/omtrek.',
      },
      {
        familyIdPattern: '^MET-MEETK-OPP$',
        preferredStimulus: 'geometricShape',
        note: 'Vorm met lengte × breedte gemarkeerd.',
      },
      {
        familyIdPattern: '^MET-DRIEHOEK-SOORTEN',
        preferredStimulus: 'geometricShape',
        note: 'Driehoek-typen visueel onderscheiden.',
      },
      {
        familyIdPattern: '^MET-SYMMETRIE',
        preferredStimulus: 'symmetryGrid',
        note: 'Symmetrie-as op een raster.',
      },
      {
        familyIdPattern: '^MET-COORDINATEN',
        preferredStimulus: 'geo',
        note: 'Coördinatenstelsel met assen.',
      },

      // — Schaal (no hard support hint; seeds use stairs/ratioTable/numberline interchangeably) —
      {
        familyIdPattern: '^MET-SCHAAL-OPP$',
        preferredSupport: 'stairs',
        note: 'Schaal-oppervlakte: kwadrateren via trap of verhoudingstabel.',
      },
      {
        familyIdPattern: '^MET-SCHAAL-',
        preferredSupport: 'ratioTable',
        note: 'Schaal-verband: verhoudingstabel is voorkeur, stairs/numberline acceptabel als didactisch passend.',
      },

      // — Oppervlakte: specifics vóór generic ^MET-OPP- —
      // MET-OPP-OMREKENEN en MET-OPP-LAND zijn omrekeningsfamilies, geen
      // vorm-met-afmetingen families — de metrieke trap is dragend.
      {
        familyIdPattern: '^MET-OPP-OMREKENEN$',
        preferredSupport: 'stairs',
        roleScope: 'rule-only',
        note: 'Oppervlakte-omrekenen: kwadratische metrieke trap (×100/÷100 per trede).',
      },
      {
        familyIdPattern: '^MET-OPP-LAND$',
        preferredSupport: 'stairs',
        roleScope: 'rule-only',
        note: 'Land-oppervlakte (ha/km²/m²): omrekenen via trap, geen vorm-stimulus nodig.',
      },
      {
        familyIdPattern: '^MET-OPP-',
        preferredStimulus: 'geometricShape',
        autoExemptEmbedded: true,
        note: 'Oppervlakte-families tonen de vorm met afmetingen.',
      },
      {
        familyIdPattern: '^MET-OMTREK-',
        preferredStimulus: 'geometricShape',
        autoExemptEmbedded: true,
        note: 'Omtrek-families tonen de vorm met alle zijden gemarkeerd.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'estimation_slider',
      'thermometer_slider',
      'grid_shade',
      'categorize',
      'step_solver',
      'error_spotting',
      'ratio_table',
      'comparison_judge',
      'ordering',
    ],
    discouragedTypes: [
      {
        type: 'true_false_series',
        reason:
          'P0 audit fix: true_false is recognition-only en past niet bij metriek.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^MET-(OPP|OMTREK|MEETK)-',
        preferredTypes: [
          'fill_in',
          'step_solver',
          'grid_shade',
          'multiple_choice',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Oppervlakte/omtrek/meetkunde: berekening of constructie vereist — geen herkenning.',
      },
      {
        familyIdPattern: '^MET-(GEW|LEN|INH)-OMREKEN',
        preferredTypes: ['fill_in', 'step_solver', 'multiple_choice'],
        discouragedTypes: ['true_false_series'],
        note: 'Omrekenen: metrieke trap doorlopen met expliciete berekening.',
      },
      {
        familyIdPattern: '^MET-(GEW|LEN|INH)-SCHATTEN',
        preferredTypes: ['estimation_slider', 'multiple_choice', 'matching'],
        note: 'Schatten: slider geeft fijne feedback over schattings-precisie.',
      },
      {
        familyIdPattern: '^MET-HOEK-(SOORTEN|BEREKENEN|METEN)',
        preferredTypes: [
          'categorize',
          'fill_in',
          'step_solver',
          'multiple_choice',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Hoeken: classificatie (scherp/recht/stomp) of berekening — beide vereisen toepassing.',
      },
      {
        familyIdPattern: '^MET-TIJD-',
        preferredTypes: ['fill_in', 'step_solver', 'multiple_choice'],
        note: 'Tijd meten/nauwkeurig: aflezen of berekenen via duur-stappen.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'unit-swap',
      label: 'Eenheid verwisselen',
      description:
        'cm i.p.v. m of cm² i.p.v. m² kiezen — kerntrap voor metriek.',
    },
    {
      id: 'area-not-squared',
      label: 'Oppervlakte zonder kwadrateren',
      description:
        'Bij schaal × 10 oppervlakte als × 10 berekenen i.p.v. × 100.',
    },
    {
      id: 'formula-confusion',
      label: 'Formule verwisselen',
      description:
        'Omtrek-formule bij oppervlakte-vraag of andersom toepassen.',
    },
  ],

  keyRules: [
    'Eenheden altijd vermelden: cm², m², km²',
    'Formules expliciet noemen in uitleg (O = l × b)',
    'Geo-visuals verplicht voor oppervlakte/omtrek families',
    'Metrieke trap als support-visual bij omrekenen',
    'Trap-distractors: eenheid verwisselen, oppervlakte zonder kwadrateren',
  ],

  authoringGuideAnchor: '#3-met--meten--meetkunde',
}
